import * as React from "react";
import { useState } from "react";
import { getContainer } from "../../ioc/IOCSetup";
import { CardBox, RouterViewTemplate, useLoadingState } from "../RouterView";
import { Form, useForm } from "../form/Form";
import { FormSubmitError, FormSubmitSuccess } from "../form/FormFeedback";
import { AdminSecuritySettings, OtpRequirement } from "../../http/protocol";
import "./SettingsGeneralView.scss";
import { SelectInput } from "../form/SelectInput";
import { Submit } from "../form/Submit";
import { FormInput } from "../form/FormInput";
import { Validators } from "../form/Validators";
import { BudgetPermissionLimitsCard } from "./adminBudgetPermissionLimits/BudgetPermissionLimitsCard";
import { t } from "../../i18n";

export const SecuritySettingsView = () => {
  const [settings, setSettings] = useState<AdminSecuritySettings | undefined>();

  const status = useLoadingState(() => {
    return getContainer()
      .getSecuritySettingsService()
      .fetchSecuritySettings()
      .then((current) => {
        setSettings(current);
      });
  }, []);

  return (
    <RouterViewTemplate status={status}>
      <CardBox title={"Admin Security Settings"}>{settings && <SettingsForm initial={settings} />}</CardBox>
      <BudgetPermissionLimitsCard />
    </RouterViewTemplate>
  );
};
const SettingsForm = (props: { initial: AdminSecuritySettings }) => {
  const [form, listener, setFormState] = useForm<AdminSecuritySettings>(props.initial, () => {
    return getContainer().getSecuritySettingsService().saveSettings(form.model);
  });

  return (
    <Form
      className={"form"}
      formListener={listener}
      modelObject={form.model}
      validationMap={form.validationMap}
    >
      <div className={"form-group"}>
        <label>Admin User Login OTP Requirement</label>
        <SelectInput
          className={"form-control"}
          model={"otpRequirement"}
          values={Object.keys(OtpRequirement)}
        />
        <span className={"help-block"}>
          <strong>NOT_REQUIRED</strong> - No verification required.
          <br />
          <strong>ALWAYS</strong> - always require the admin users to enter verification code.
          <br />
          <strong>PER_DEVICE</strong> - require an admin to enter a verification code once per device per 30 days.
          <br />
        </span>
      </div>
      <div className={"form-group"}>
        <label>Large Amount Threshold (EUR)</label>
        <FormInput
          className={"form-control"}
          model={"largeAmountThreshold"}
          type="float"
          validators={[Validators.number(), Validators.min(0)]}
        />
        <span className={"help-block"}>
          Set the threshold in EUR for large amount confirmations. When users enter amounts exceeding this value, a confirmation dialog will be shown with the
          original amount and EUR equivalent.
          <br />
          <strong>0</strong> - Validation disabled (no confirmation dialogs).
          <br />
          <strong>&gt; 0</strong> - Trigger confirmation for amounts exceeding this threshold.
        </span>
      </div>
      <div className={"form-group"}>
        <div className="custom-control custom-checkbox">
          <FormInput
            className="custom-control-input"
            model="currencyEuroValueEnabled"
            id={"currencyEuroValueEnabled"}
            type="checkbox"
          />
          <label
            htmlFor={"currencyEuroValueEnabled"}
            className={"custom-control-label"}
          >
            Currency Euro Value Display
          </label>
        </div>
        <span className={"help-block"}>Show EUR equivalent value in currency input forms in the admin interface</span>
      </div>
      <hr />
      <h5>{t("securitySettings.cashbackFraction.title", undefined, "Large Cashback & Rakeback Fraction Limits")}</h5>
      <p className={"help-block"}>
        {t(
          "securitySettings.cashbackFraction.help",
          undefined,
          "A cashback or rakeback configuration's fraction multiplies a per-player contribution. When an entered fraction reaches the warning limit for its reward type and calculation basis, the config form shows an advisory warning; the blocking ceiling refuses the save outright. RTP_BASED multiplies only the house-edge slice of a wager and so tolerates a far larger fraction than UNWEIGHTED, which multiplies the whole wager — and rakeback fractions run much smaller than cashback ones, hence a separate set of limits for each.",
        )}
      </p>
      <table className={"table"}>
        <thead>
          <tr>
            <th scope="col"></th>
            <th scope="col">{t("securitySettings.cashbackFraction.rakeback.heading", undefined, "Rakeback")}</th>
            <th scope="col">{t("securitySettings.cashbackFraction.cashback.heading", undefined, "Cashback")}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">{t("securitySettings.cashbackFraction.rtp-row-label", undefined, "RTP-based basis warning limit")}</th>
            <td>
              <FormInput
                className={"form-control"}
                model={"largeRakebackFractionRtp"}
                type="float"
                // Rakeback fractions mirror the backend bound: greater than 0 and at most 1. 0.000001 is the
                // smallest positive the DECIMAL(19,6) column can hold, so this rejects 0.
                validators={[Validators.number(), Validators.range(0.000001, 1)]}
                data-testid="security-large-rakeback-fraction-rtp"
              />
              <span className={"help-block"}>
                {t(
                  "securitySettings.cashbackFraction.rakeback.rtp-help",
                  undefined,
                  "Warn when an RTP_BASED rakeback fraction reaches this value. Default 0.05.",
                )}
              </span>
            </td>
            <td>
              <FormInput
                className={"form-control"}
                model={"largeCashbackFractionRtp"}
                type="float"
                // Cashback fractions may legitimately exceed 1 (a payout larger than the tracked
                // contribution), so only the positive lower bound is enforced here.
                validators={[Validators.number(), Validators.min(0.000001)]}
                data-testid="security-large-cashback-fraction-rtp"
              />
              <span className={"help-block"}>
                {t(
                  "securitySettings.cashbackFraction.cashback.rtp-help",
                  undefined,
                  "Warn when an RTP_BASED cashback fraction reaches this value. Default 0.5.",
                )}
              </span>
            </td>
          </tr>
          <tr>
            <th scope="row">{t("securitySettings.cashbackFraction.unweighted-row-label", undefined, "Unweighted basis warning limit")}</th>
            <td>
              <FormInput
                className={"form-control"}
                model={"largeRakebackFractionUnweighted"}
                type="float"
                validators={[Validators.number(), Validators.range(0.000001, 1)]}
                data-testid="security-large-rakeback-fraction-unweighted"
              />
              <span className={"help-block"}>
                {t(
                  "securitySettings.cashbackFraction.rakeback.unweighted-help",
                  undefined,
                  "Warn when an UNWEIGHTED rakeback fraction reaches this value. Default 0.001.",
                )}
              </span>
            </td>
            <td>
              <FormInput
                className={"form-control"}
                model={"largeCashbackFractionUnweighted"}
                type="float"
                validators={[Validators.number(), Validators.min(0.000001)]}
                data-testid="security-large-cashback-fraction-unweighted"
              />
              <span className={"help-block"}>
                {t(
                  "securitySettings.cashbackFraction.cashback.unweighted-help",
                  undefined,
                  "Warn when an UNWEIGHTED cashback fraction reaches this value. Default 0.2.",
                )}
              </span>
            </td>
          </tr>
          <tr>
            <th scope="row">{t("securitySettings.cashbackFraction.blocking-row-label", undefined, "Maximum fraction (blocking)")}</th>
            <td>
              <FormInput
                className={"form-control"}
                model={"maxRakebackFraction"}
                type="float"
                validators={[Validators.number(), Validators.range(0.000001, 1)]}
                data-testid="security-max-rakeback-fraction"
              />
              <span className={"help-block"}>
                {t(
                  "securitySettings.cashbackFraction.rakeback.blocking-help",
                  undefined,
                  "Reject saving a rakeback configuration when a fraction is changed to a value above this ceiling. Applies to any calculation basis, and sits above the advisory warning limits. Fractions already stored above it are grandfathered until changed. Default 0.1.",
                )}
              </span>
            </td>
            <td>
              <FormInput
                className={"form-control"}
                model={"maxCashbackFraction"}
                type="float"
                validators={[Validators.number(), Validators.min(0.000001)]}
                data-testid="security-max-cashback-fraction"
              />
              <span className={"help-block"}>
                {t(
                  "securitySettings.cashbackFraction.cashback.blocking-help",
                  undefined,
                  "Reject saving a cashback configuration when a fraction is changed to a value above this ceiling. Applies to any calculation basis, and sits above the advisory warning limits. Fractions already stored above it are grandfathered until changed. Default 1.0.",
                )}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
      <div>
        <FormSubmitError />
        <FormSubmitSuccess text="Settings saved successfully" />
        <Submit className={"btn btn-primary"}>Save</Submit>
      </div>
    </Form>
  );
};
